import React, { useRef, useEffect, useState, useCallback } from 'react'
import Tesseract from 'tesseract.js'
import {
	Camera,
	Printer as LucidePrinter,
	Check,
	AlertCircle,
	RefreshCw,
	Box as LucideBox,
	Layers,
} from 'lucide-react'
import axios from 'axios'
import { databaseLocation } from '../../dummyData'
import exampleImage from '../../images/example.jpeg'
import { DataGrid } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import { DeleteOutline, Search, Add, Remove, Print } from '@mui/icons-material'
import { Link, useLocation } from 'react-router-dom'
import Dropdown from '../../components/Dropdown'
import { useLog } from '../../contexts/LogContext'
import { Stack, IconButton, Tooltip, Badge } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SearchIcon from '@mui/icons-material/Search'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import StorefrontIcon from '@mui/icons-material/Storefront'
import HandymanIcon from '@mui/icons-material/Handyman'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import PrintIcon from '@mui/icons-material/Print'
import api from '../../axios'

const CameraScanner = () => {
	const videoRef = useRef(null)
	const canvasRef = useRef(null)
	const [isScanning, setIsScanning] = useState(false)
	const [scanResult, setScanResult] = useState('')
	const [error, setError] = useState('')
	const [status, setStatus] = useState('Idle')
	const [worker, setWorker] = useState(null)
	const [activeStream, setActiveStream] = useState(null)
	const [capturedImage, setCapturedImage] = useState(null)
	const [debugImage, setDebugImage] = useState(null)
	const [debugText, setDebugText] = useState('')
	const [showDebug, setShowDebug] = useState(false)

	// DataGrid State
	const [articleList, setArticleList] = useState([])
	const [filteredList, setFilteredList] = useState([])
	const [searchText, setSearchText] = useState('')
	const [selectedFilters, setSelectedFilters] = useState([])
	const [loading, setLoading] = useState(true)
	const { createLogEntry } = useLog() // Hook from context

	// Camera Selection State
	const [devices, setDevices] = useState([])
	const [currentDeviceId, setCurrentDeviceId] = useState('')

	// Printer Selection State
	const [printers, setPrinters] = useState([])
	const [selectedPrinter, setSelectedPrinter] = useState('')

	const playBeep = () => {
		const audio = new Audio('https://actions.google.com/sounds/v1/alarms/beep_short.ogg')
		audio.play().catch((e) => console.log('Audio play failed', e))
	}

	const stopCamera = useCallback(() => {
		if (activeStream) {
			activeStream.getTracks().forEach((t) => t.stop())
			setActiveStream(null)
		}
		if (videoRef.current) videoRef.current.srcObject = null
		setIsScanning(false)
		setStatus('Stopped')
	}, [activeStream])

	const buildPshUrl = (num) =>
		`https://psh.eu/en/search-results/?searched_type=start&q=${encodeURIComponent(
			num || ''
		)}&search+form+submit=&Brand=&product_group=&kntype=&kntype-number=`

	const buildKfz24Url = (num, type) => {
		const t = (type || '').toLowerCase()
		if (/alternator|lichtmaschine/.test(t)) {
			return `https://www.kfzteile24.de/ersatzteile-verschleissteile/elektrik/generator/generator-lichtmaschine?notTypeSearch=${encodeURIComponent(
				num || ''
			)}`
		}
		if (/starter|anlasser/.test(t)) {
			return `https://www.kfzteile24.de/artikelsuche?search=${encodeURIComponent(
				num || ''
			)}&searchType=artnrOenr`
		}
		return null
	}

	const handlePrint = async (article) => {
		if (!selectedPrinter) {
			alert('Please select a printer first.')
			return
		}
		try {
			await axios.post('http://localhost:5000/print/', {
				articleNumber: article.articleNumber,
				printerName: selectedPrinter,
			})
			createLogEntry(`printed label for ${article.articleNumber}`)
			// Optional: Show success feedback toast
		} catch (e) {
			console.error('Print failed', e)
			alert('Failed to print label.')
		}
	}

	const updateQuantityHandler = async (article, change) => {
		const newQuantity = article.quantity + change
		if (newQuantity < 0) return

		await api
			.put(`/api/updateQuantity`, {
				quantity: newQuantity,
				articleId: article.articleId,
				username: 'scanner', // Default or fetch from user context if available
			})
			.then((res) => {
				createLogEntry(`updated quantity of "${article.articleNumber}" to ${newQuantity}`)
				article.quantity = newQuantity
				updateArticleInList(article)
			})
			.catch((e) => console.log(e))
	}

	const updateArticleInList = (article) => {
		const newList = articleList.map((item) => {
			if (item.articleId === article.articleId) {
				return { ...item, quantity: article.quantity }
			}
			return item
		})
		setArticleList(newList)
		// Update filtered list too if needed, but search effect might handle it or we need to sync
		setFilteredList((prev) =>
			prev.map((item) =>
				item.articleId === article.articleId
					? { ...item, quantity: article.quantity }
					: item
			)
		)
	}

	const columns = [
		{ field: 'articleType', headerName: 'Type', minWidth: 150, flex: 1, resizable: true },
		{ field: 'brand', headerName: 'Brand', minWidth: 100, flex: 1, resizable: true },
		{ field: 'power', headerName: 'Power', minWidth: 80, flex: 0.5 },
		{ field: 'storagePlace', headerName: 'Place', minWidth: 120, flex: 0.8 },
		{ field: 'quantity', headerName: 'Qty', minWidth: 80, flex: 0.5 },
		{ field: 'articleNumber', headerName: 'Article', minWidth: 150, flex: 1 },
		{ field: 'manufacturer', headerName: 'Manufacturer', minWidth: 120, flex: 0.8 },
		{ field: 'ebayPlus', headerName: 'Price', minWidth: 100, numeric: true, flex: 0.8 },
		{
			field: 'onEbay',
			headerName: 'onEbay',
			minWidth: 80,
			flex: 0.8,
			renderCell: (params) => {
				const num = params.row.articleNumber
				const count = Number(params.value || 0)
				return (
					<Box sx={{ display: 'flex', width: '100%' }}>
						<Tooltip title='eBay'>
							<Badge badgeContent={count} color={count > 0 ? 'success' : 'default'}>
								<IconButton
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										window.open(
											`https://www.ebay.de/sch/i.html?_nkw=${encodeURIComponent(
												num
											)}`,
											'_blank'
										)
									}}>
									<StorefrontIcon fontSize='small' />
								</IconButton>
							</Badge>
						</Tooltip>
					</Box>
				)
			},
		},
		{
			field: 'onShopify',
			headerName: 'onShopify',
			minWidth: 80,
			flex: 0.8,
			renderCell: (params) => {
				const num = params.row.articleNumber
				const count = Number(params.value || 0)
				return (
					<Box sx={{ display: 'flex', width: '100%' }}>
						<Tooltip title='MD-Teile'>
							<Badge badgeContent={count} color={count > 0 ? 'success' : 'default'}>
								<IconButton
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										window.open(
											`https://md-teile.de/search?q=${encodeURIComponent(
												num
											)}`,
											'_blank'
										)
									}}>
									<HandymanIcon fontSize='small' />
								</IconButton>
							</Badge>
						</Tooltip>
					</Box>
				)
			},
		},
		{
			field: 'URL',
			headerName: 'Image',
			minWidth: 50,
			flex: 0.8,
			renderCell: (params) => {
				const src = params.row.URL
				if (!src || src.length < 3) return null
				return (
					<Tooltip
						title={
							<Box
								sx={{
									borderRadius: 1,
									overflow: 'hidden',
									bgcolor: 'background.paper',
								}}>
								<img src={src} style={{ display: 'block', width: 280 }} />
							</Box>
						}>
						<img className='size-14 rounded-full object-cover' src={src} />
					</Tooltip>
				)
			},
		},
		{
			field: 'action',
			headerName: 'Actions',
			minWidth: 200,
			flex: 1.2,
			renderCell: (params) => {
				const num = params.row.articleNumber
				const kfzUrl = buildKfz24Url(num, params.row.articleType)
				return (
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Tooltip title='Increase Qty'>
							<IconButton
								size='small'
								onClick={() => updateQuantityHandler(params.row, 1)}>
								<AddIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title='Decrease Qty'>
							<IconButton
								size='small'
								onClick={() => updateQuantityHandler(params.row, -1)}>
								<RemoveIcon />
							</IconButton>
						</Tooltip>

						{/* Print Button */}
						<Tooltip title='Print Label'>
							<IconButton
								size='small'
								onClick={() => handlePrint(params.row)}
								sx={{ color: '#4b5563', '&:hover': { color: '#000' } }}>
								<PrintIcon />
							</IconButton>
						</Tooltip>

						{kfzUrl && (
							<IconButton size='small' onClick={() => window.open(kfzUrl, '_blank')}>
								<DirectionsCarFilledIcon />
							</IconButton>
						)}
					</Box>
				)
			},
		},
	]

	const filterOptions = ['NotOnEbay', 'WithPicture', 'WithoutPicture', 'NoRegulator', 'InStock']

	const handleFilter = (value) => {
		if (value.length < 1) {
			setSelectedFilters([])
			setFilteredList(articleList)
		} else {
			if (!selectedFilters.includes(value)) {
				setSelectedFilters([...selectedFilters, value])
			} else {
				setSelectedFilters(selectedFilters.filter((option) => option != value))
			}
			let tempList = articleList
			if (value.includes('NotOnEbay'))
				tempList = tempList.filter((article) => article.onEbay == 0)
			if (value.includes('WithPicture'))
				tempList = tempList.filter((article) => article.URL.length > 3)
			if (value.includes('WithoutPicture'))
				tempList = tempList.filter((article) => article.URL.length < 3)
			if (value.includes('NoRegulator'))
				tempList = tempList.filter(
					(article) => !article.articleType.toLowerCase().includes('regulator')
				)
			if (value.includes('InStock'))
				tempList = tempList.filter((article) => article.quantity > 0)
			setFilteredList(tempList)
		}
	}

	// Initialize Data
	useEffect(() => {
		let isMounted = true
		setLoading(true)
		const fetchProducts = async () => {
			try {
				const res = await axios.get(`${databaseLocation}/product/`)
				if (res.data && isMounted) {
					// console.log('[Scanner] Loaded Products:', res.data.length)
					setArticleList(res.data)
					setFilteredList(res.data)
				}
			} catch (e) {
				console.error('Failed to load products', e)
				setError('Could not load product database.')
			} finally {
				if (isMounted) setLoading(false)
			}
		}
		fetchProducts()

		// Init Tesseract (moved here to keep clean)
		const initWorker = async () => {
			try {
				const w = await Tesseract.createWorker('eng')
				await w.setParameters({
					tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.:-',
					tessedit_pageseg_mode: '3',
				})
				setWorker(w)
			} catch (err) {
				console.error('Failed to create worker', err)
				setError('OCR Initialization Failed')
			}
		}
		initWorker()

		// Fetch Printers
		const fetchPrinters = async () => {
			try {
				const res = await axios.get('http://localhost:5000/print/printers')
				if (res.data && Array.isArray(res.data)) {
					setPrinters(res.data)
					// Prioritize QL-800, then Brother, then first available
					const ql800 = res.data.find((p) => p.Name.includes('QL-800'))
					const brother = res.data.find((p) => p.Name.includes('Brother'))

					if (ql800) {
						setSelectedPrinter(ql800.Name)
					} else if (brother) {
						setSelectedPrinter(brother.Name)
					} else {
						setSelectedPrinter(res.data[0]?.Name || '')
					}
				}
			} catch (e) {
				console.warn('Failed to fetch printers', e)
			}
		}
		fetchPrinters()

		return () => {
			isMounted = false
		}
	}, [])

	// Search Side Effect
	useEffect(() => {
		const searchTxt = searchText.toLowerCase().replaceAll(' ', '')
		setFilteredList(
			articleList.filter(
				(article) =>
					article.articleNumber.replaceAll(' ', '').toLowerCase().includes(searchTxt) ||
					(article.oe && article.oe.replaceAll(' ', '').toLowerCase().includes(searchTxt))
			)
		)
		if (searchText.length > 3) {
			// Optional: log search
			// createLogEntry(`scanner searched for "${searchText}"`)
		}
	}, [searchText, articleList])

	// Handle Scan Success -> SEARCH
	const handleScanSuccess = useCallback(
		(originalCode) => {
			let code = originalCode.replace(/\s/g, '').toUpperCase()
			setScanResult(originalCode)
			playBeep()

			// Iterative Fuzzy Search
			console.log(`[Search] Starting search for: ${code}`)
			let found = null
			let currentCode = code

			while (currentCode.length >= 5) {
				console.log(`[Search] Trying: ${currentCode}...`)
				const match = articleList.find((p) => {
					const cleanArticle = p.articleNumber
						? p.articleNumber.replace(/\s/g, '').toUpperCase()
						: ''
					const cleanOE = p.oe ? p.oe.replace(/\s/g, '').toUpperCase() : ''
					return cleanArticle.includes(currentCode) || cleanOE.includes(currentCode)
				})
				if (match) {
					console.log(`[Search] Match Found! => ${match.articleNumber}`)
					found = match
					break
				}
				console.log(`[Search] No match for ${currentCode}. trimming...`)
				currentCode = currentCode.slice(0, -1) // Remove last char
			}

			if (found) {
				setStatus(`Found: ${found.articleNumber}`)
				// Set search text to the FOUND code (or the exact article number)
				// Using articleNumber ensures the grid filters to the exact item(s)
				setSearchText(found.articleNumber)
			} else {
				setStatus(`Scanned: ${originalCode} (Not in Database)`)
				// Even if not found, we can set search text to what we scanned so user sees "empty" grid
				setSearchText(originalCode)
			}
		},
		[articleList]
	)

	const captureImage = () => {
		if (!videoRef.current || !canvasRef.current) return
		const video = videoRef.current
		const canvas = canvasRef.current
		const context = canvas.getContext('2d')

		canvas.width = video.videoWidth
		canvas.height = video.videoHeight
		context.drawImage(video, 0, 0, canvas.width, canvas.height)

		setCapturedImage(canvas.toDataURL('image/jpeg'))
		setIsScanning(false)
		stopCamera()
		scanImage(canvas)
	}

	const scanImage = async (sourceCanvas) => {
		if (!worker) return
		setStatus('Scanning Image...')
		setError('')
		setScanResult('')

		setDebugText('')

		// Crop & Scale
		const scanWidth = sourceCanvas.width * 0.8
		const scanHeight = sourceCanvas.height * 0.3
		const scanX = (sourceCanvas.width - scanWidth) / 2
		const scanY = (sourceCanvas.height - scanHeight) / 2

		const scale = 2
		const tempCanvas = document.createElement('canvas')
		tempCanvas.width = scanWidth * scale
		tempCanvas.height = scanHeight * scale
		const ctx = tempCanvas.getContext('2d')

		ctx.drawImage(
			sourceCanvas,
			scanX,
			scanY,
			scanWidth,
			scanHeight,
			0,
			0,
			tempCanvas.width,
			tempCanvas.height
		)

		// Grayscale
		const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
		const data = imageData.data
		for (let i = 0; i < data.length; i += 4) {
			const avg = (data[i] + data[i + 1] + data[i + 2]) / 3
			data[i] = avg
			data[i + 1] = avg
			data[i + 2] = avg
		}
		ctx.putImageData(imageData, 0, 0)

		setDebugImage(tempCanvas.toDataURL('image/png'))

		try {
			const {
				data: { lines, text },
			} = await worker.recognize(tempCanvas)
			setDebugText(text)
			console.log('OCR Text:', text)

			let bestCandidate = null
			let bestConf = 0

			const textLines = text && text.length > 0 ? text.split('\n') : []
			const linesToProcess =
				lines && lines.length > 0
					? lines
					: textLines.map((l) => ({ text: l, confidence: 50 }))

			for (const line of linesToProcess) {
				let lineText = line.text.replace(/\s/g, '').toUpperCase()
				if (lineText.length < 5) continue

				// 1. FG Start
				const fgMatch = lineText.match(/FG[A-Z0-9]{5,15}/)
				if (fgMatch) {
					handleScanSuccess(fgMatch[0])
					return
				}

				// 2. Fallback
				const match = lineText.match(/[A-Z0-9]{6,15}/)
				if (match && line.confidence > 60) {
					if (line.confidence > bestConf) {
						bestConf = line.confidence
						bestCandidate = match[0]
					}
				}
			}

			if (bestCandidate) {
				handleScanSuccess(bestCandidate)
			} else {
				setStatus('No code found. Retake.')
			}
		} catch (e) {
			console.error('OCR Error', e)
			setError('Analysis Failed')
		}
	}

	const startCamera = async (specificDeviceId = null) => {
		if (activeStream) activeStream.getTracks().forEach((t) => t.stop())
		setCapturedImage(null)
		setScanResult('')

		setError('')
		setDebugImage(null)
		setDebugText('')

		try {
			setStatus('Initializing...')
			let constraints = { video: { width: { ideal: 1920 }, height: { ideal: 1080 } } }
			const targetId = specificDeviceId || currentDeviceId
			if (targetId) constraints.video.deviceId = { exact: targetId }

			const stream = await navigator.mediaDevices.getUserMedia(constraints)

			// Re-enumerate
			const all = await navigator.mediaDevices.enumerateDevices()
			const videos = all.filter((d) => d.kind === 'videoinput')
			setDevices(videos)

			const track = stream.getVideoTracks()[0]
			if (track) {
				const settings = track.getSettings()
				if (settings.deviceId) setCurrentDeviceId(settings.deviceId)
			}

			setActiveStream(stream)
			setIsScanning(true)
			setStatus('Camera Ready')
		} catch (err) {
			console.error(err)
			setError('Camera Access Failed')
		}
	}

	useEffect(() => {
		if (isScanning && activeStream && videoRef.current) {
			videoRef.current.srcObject = activeStream
			videoRef.current.play().catch(console.error)
		}
	}, [isScanning, activeStream])

	const retake = () => {
		setCapturedImage(null)
		setScanResult('')
		setDebugImage(null)
		startCamera()
	}

	const testSampleImage = () => {
		if (!worker) return
		const img = new Image()
		img.src = exampleImage
		img.onload = () => {
			const canvas = document.createElement('canvas')
			canvas.width = img.width
			canvas.height = img.height
			const ctx = canvas.getContext('2d')
			ctx.drawImage(img, 0, 0)

			setCapturedImage(canvas.toDataURL('image/jpeg'))
			setIsScanning(false)
			if (activeStream) activeStream.getTracks().forEach((t) => t.stop())
			scanImage(canvas)
		}
	}

	return (
		<div className='flex flex-col items-center min-h-[calc(100vh-64px)] bg-gray-100 p-4 gap-6'>
			{/* Camera Card */}
			<div className='w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden'>
				{/* Header */}
				<div className='p-6 border-b border-gray-100 flex flex-col gap-4'>
					<div className='flex justify-between items-center'>
						<h2 className='text-2xl font-bold text-gray-800 flex items-center gap-2'>
							<Camera className='w-6 h-6 text-blue-600' />
							Smart Scanner
						</h2>
						{/* Printer Dropdown */}
						<div className='flex items-center gap-2'>
							<LucidePrinter className='w-4 h-4 text-gray-500' />
							<select
								value={selectedPrinter}
								onChange={(e) => setSelectedPrinter(e.target.value)}
								className='text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500'>
								<option value=''>Default Printer</option>
								{printers.map((p) => (
									<option key={p.Name} value={p.Name}>
										{p.Name}
									</option>
								))}
							</select>
						</div>
					</div>

					{/* Camera Dropdown */}
					{devices.length > 0 && !capturedImage && (
						<div className='w-full'>
							<select
								className='w-full appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer'
								value={currentDeviceId}
								onChange={(e) => {
									setCurrentDeviceId(e.target.value)
									startCamera(e.target.value)
								}}>
								{devices.map((d, i) => (
									<option key={d.deviceId} value={d.deviceId}>
										{d.label || `Camera ${i + 1}`}
									</option>
								))}
							</select>
						</div>
					)}
				</div>

				{/* Main Viewport */}
				<div className='relative bg-black aspect-video flex items-center justify-center overflow-hidden'>
					{/* 1. Start Button */}
					{!isScanning && !capturedImage && (
						<button
							onClick={() => startCamera()}
							className='bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 z-10'>
							<Camera className='w-5 h-5' /> Start Camera
						</button>
					)}

					{/* 2. Live Video */}
					{isScanning && !capturedImage && (
						<>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className='w-full h-full object-cover'
							/>
							{/* ROI Overlay */}
							<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
								<div className='w-[80%] h-[25%] border-2 border-white/50 rounded-lg shadow-[0_0_0_999px_rgba(0,0,0,0.5)]'></div>
							</div>

							<button
								onClick={captureImage}
								className='absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-full p-4 border-4 border-gray-200 hover:border-blue-500 transition-all z-20 shadow-lg cursor-pointer pointer-events-auto'
								title='Take Photo'>
								<div className='w-6 h-6 bg-black rounded-full'></div>
							</button>
						</>
					)}

					{/* 3. Captured */}
					{capturedImage && (
						<>
							<img
								src={capturedImage}
								alt='Captured'
								className='w-full h-full object-contain'
							/>

							{/* Debug Overlay */}
							{showDebug && debugImage && (
								<div className='absolute top-0 right-0 p-2 bg-black/80 border border-white z-30 max-w-[50%]'>
									<p className='text-xs text-white mb-1'>OCR Input:</p>
									<img
										src={debugImage}
										alt='Debug Crop'
										className='w-full border border-gray-500'
									/>
									<p className='text-[10px] text-green-400 mt-1 font-mono break-all leading-tight'>
										{debugText || 'Processing...'}
									</p>
								</div>
							)}
						</>
					)}
					<canvas ref={canvasRef} className='hidden' />
				</div>

				{/* Footer Controls */}
				<div className='p-6 bg-gray-50 flex items-center justify-between'>
					<div className='flex items-center gap-3'>
						<div
							className={`w-3 h-3 rounded-full ${
								isScanning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
							}`}></div>
						<span className='font-medium text-gray-700'>{status}</span>
					</div>

					<div className='flex gap-2'>
						{capturedImage && (
							<button
								onClick={() => setShowDebug(!showDebug)}
								className='text-gray-500 hover:text-gray-800 font-medium text-xs border border-gray-300 px-2 py-1 rounded'>
								{showDebug ? 'Hide' : 'Debug'}
							</button>
						)}

						{capturedImage && (
							<button
								onClick={retake}
								className='text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1'>
								<RefreshCw className='w-4 h-4' /> Retake
							</button>
						)}
					</div>

					{!capturedImage && !isScanning && (
						<button
							onClick={testSampleImage}
							className='text-xs text-gray-400 hover:text-gray-600 absolute bottom-2 right-2'>
							Test Sample
						</button>
					)}
				</div>

				{error && (
					<div className='p-4 bg-red-50 border-t border-red-100 text-red-700 flex items-start gap-3'>
						<AlertCircle className='w-5 h-5 shrink-0 mt-0.5' />
						<p>{error}</p>
					</div>
				)}
			</div>

			{/* Data Grid Section */}
			<div className='w-full max-w-7xl bg-white rounded-xl shadow-lg p-6'>
				<div className='flex flex-col md:flex-row gap-4 mb-4'>
					{/* Search Input */}
					<div className='flex-grow'>
						<input
							type='text'
							placeholder='Search article...'
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							className='border border-gray-300 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all'
						/>
					</div>

					{/* Filter Dropdown */}
					<Dropdown
						options={filterOptions}
						selectedOptions={selectedFilters}
						onSelect={handleFilter}
					/>
				</div>

				{/* DataGrid */}
				<Box
					sx={{
						width: '100%',
						height: 500, // Fixed height or calc
						'& .MuiDataGrid-root': {
							border: 'none',
						},
						'& .MuiDataGrid-cell': {
							whiteSpace: 'normal',
							wordWrap: 'break-word',
						},
						'& .MuiDataGrid-columnHeaders': {
							backgroundColor: '#f7f7f7',
						},
					}}>
					<DataGrid
						rowHeight={56}
						loading={loading}
						rows={filteredList}
						getRowId={(row) => row.articleId}
						columns={columns}
						pageSize={15}
						rowsPerPageOptions={[15]}
						disableSelectionOnClick
						columnBuffer={2}
					/>
				</Box>
			</div>
		</div>
	)
}

export default CameraScanner
